import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  onFileUpload?: (file: File) => void;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = 'Type here...',
  onFileUpload
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        style: 'min-height: 120px; padding: 12px; outline: none;'
      },
    },
  });

  // Sync editor content when prop changes (e.g., when clearing)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onFileUpload) {
      onFileUpload(file);
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div style={{
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      overflow: 'hidden',
      backgroundColor: 'white',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '8px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '14px',
            fontWeight: editor.isActive('bold') ? '700' : '600',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: editor.isActive('bold') ? '#3b82f6' : 'white',
            color: editor.isActive('bold') ? 'white' : '#374151',
            cursor: 'pointer',
            minWidth: '32px'
          }}
          title="Bold"
        >
          B
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '14px',
            fontStyle: 'italic',
            fontWeight: '600',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: editor.isActive('italic') ? '#3b82f6' : 'white',
            color: editor.isActive('italic') ? 'white' : '#374151',
            cursor: 'pointer',
            minWidth: '32px'
          }}
          title="Italic"
        >
          I
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '14px',
            textDecoration: 'underline',
            fontWeight: '600',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: editor.isActive('underline') ? '#3b82f6' : 'white',
            color: editor.isActive('underline') ? 'white' : '#374151',
            cursor: 'pointer',
            minWidth: '32px'
          }}
          title="Underline"
        >
          U
        </button>

        <button
          type="button"
          onClick={setLink}
          className={editor.isActive('link') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '14px',
            fontWeight: '600',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: editor.isActive('link') ? '#3b82f6' : 'white',
            color: editor.isActive('link') ? 'white' : '#374151',
            cursor: 'pointer'
          }}
          title="Add Link"
        >
          🔗
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '14px',
            fontWeight: '600',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: editor.isActive('bulletList') ? '#3b82f6' : 'white',
            color: editor.isActive('bulletList') ? 'white' : '#374151',
            cursor: 'pointer'
          }}
          title="Bullet List"
        >
          •
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '14px',
            fontWeight: '600',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: editor.isActive('orderedList') ? '#3b82f6' : 'white',
            color: editor.isActive('orderedList') ? 'white' : '#374151',
            cursor: 'pointer'
          }}
          title="Numbered List"
        >
          1.
        </button>

        {onFileUpload && (
          <>
            <input
              type="file"
              id="file-upload"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            />
            <button
              type="button"
              onClick={() => document.getElementById('file-upload')?.click()}
              style={{
                padding: '4px 8px',
                fontSize: '14px',
                fontWeight: '600',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer'
              }}
              title="Upload File"
            >
              📎
            </button>
          </>
        )}
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
